import { LANGUAGE_OPTIONS } from '../locales/content.js';

function CompanyLogo() {
  return (
    <img
      src="/logo.svg"
      alt="Teniz Capital Investment Banking"
      className="h-10 w-auto"
    />
  );
}

function LanguageSwitcher({ language, onChange }) {
  return (
    <div
      className="inline-flex overflow-hidden rounded-lg border border-[var(--border)] bg-white"
      role="group"
      aria-label="Language"
    >
      {LANGUAGE_OPTIONS.map((option) => {
        const isActive = option.code === language;

        return (
          <button
            key={option.code}
            type="button"
            onClick={() => onChange(option.code)}
            className={`px-2.5 py-1.5 text-[12px] font-semibold transition ${
              isActive
                ? 'bg-[var(--accent)] text-white'
                : 'text-[var(--muted)] hover:bg-[var(--accent-soft)] hover:text-[var(--text)]'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="mb-3 text-[20px] font-bold leading-tight text-[var(--accent)]">
      {children}
    </h2>
  );
}

function InfoTable({ rows }) {
  return (
    <table className="mb-6 w-full border-collapse overflow-hidden border border-[var(--border)] border-t-[3px] border-t-[var(--accent)] text-[14px]">
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <th
              scope="row"
              className="w-[34%] border border-[var(--border)] bg-[var(--label-bg)] px-3 py-2.5 text-left align-top font-semibold"
            >
              {row.label}
            </th>
            <td className="border border-[var(--border)] bg-white px-3 py-2.5 align-top">
              {row.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ConfirmationForm({
  form,
  fieldErrors = {},
  fields,
  onChange,
  disabled = false,
}) {
  const fieldConfigs = [
    {
      id: 'fullName',
      label: fields.fullName,
      autoComplete: 'name',
      inputMode: 'text',
      maxLength: 200,
    },
    {
      id: 'iinBin',
      label: fields.iinBin,
      autoComplete: 'off',
      inputMode: 'numeric',
      maxLength: 12,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {fieldConfigs.map((field) => {
        const hasError = Boolean(fieldErrors[field.id]);

        return (
          <div key={field.id}>
            <label
              htmlFor={field.id}
              className="mb-1.5 block text-[14px] font-medium"
            >
              {field.label}
            </label>
            <input
              id={field.id}
              name={field.id}
              type="text"
              inputMode={field.inputMode}
              maxLength={field.maxLength}
              autoComplete={field.autoComplete}
              value={form[field.id]}
              onChange={onChange(field.id)}
              disabled={disabled}
              aria-invalid={hasError}
              className={`block w-full rounded-lg border bg-white px-3 py-2.5 text-[14.5px] outline-none transition disabled:cursor-not-allowed disabled:opacity-60 ${
                hasError
                  ? 'border-[#d92d20] focus:border-[#d92d20] focus:ring-2 focus:ring-[#d92d20]/15'
                  : 'border-[#c9d4d6] focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-soft)]'
              }`}
            />
            {hasError ? (
              <p className="mt-1.5 text-[12px] text-[#d92d20]">
                {fieldErrors[field.id]}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

function SuccessModal({ title, closeLabel, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="success-title"
    >
      <div className="relative w-full max-w-[420px] rounded-2xl bg-white px-8 py-10 text-center shadow-lg">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 flex h-10 w-10 items-center justify-center text-[var(--muted)] transition hover:text-[var(--text)]"
          aria-label={closeLabel}
        >
          <svg
            viewBox="0 0 24 24"
            className="h-7 w-7"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#2e7d32]">
          <svg
            viewBox="0 0 24 24"
            className="h-8 w-8 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p id="success-title" className="text-[16px] font-medium">
          {title}
        </p>
      </div>
    </div>
  );
}

export default function RiskNoticePage({
  form,
  fieldErrors = {},
  status = 'idle',
  errorMessage = '',
  locale,
  language,
  onLanguageChange,
  onChange,
  onSubmit,
  onCloseSuccess,
}) {
  const isLoading = status === 'loading';
  const isSuccess = status === 'success';
  const isError = status === 'error';
  const isSubmitDisabled = isLoading;

  return (
    <div className="min-h-screen px-4 py-8 sm:px-6">
      <form
        onSubmit={onSubmit}
        className="mx-auto max-w-[860px] rounded-sm bg-white px-6 py-8 shadow-sm sm:px-10 sm:py-10"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <LanguageSwitcher language={language} onChange={onLanguageChange} />
          <CompanyLogo />
        </div>

        <header className="mb-6 text-center">
          <h1 className="text-[28px] font-bold uppercase tracking-[0.02em] text-[var(--accent)]">
            {locale.title}
          </h1>
          <p className="mx-auto mt-2 max-w-[640px] text-[16px] font-semibold leading-snug">
            {locale.subtitle}
          </p>
        </header>

        <InfoTable rows={locale.infoRows} />

        <p className="mb-4 text-[14.5px]">{locale.intro}</p>

        <p className="mb-8 text-[14.5px]">
          <strong>{locale.importantLabel}</strong> {locale.important}
        </p>

        <section className="mb-8">
          <SectionTitle>{locale.section1Title}</SectionTitle>
          {locale.section1Paragraphs.map((paragraph) => (
            <p key={paragraph} className="mb-3 text-[14.5px]">
              {paragraph}
            </p>
          ))}
          <ol className="mb-3 list-decimal space-y-2 pl-5 text-[14.5px]">
            {locale.excludedNorms.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                {item.rest}
              </li>
            ))}
          </ol>
          {locale.section1Closing.map((paragraph, index) => (
            <p
              key={paragraph}
              className={`text-[14.5px] ${
                index < locale.section1Closing.length - 1 ? 'mb-3' : ''
              }`}
            >
              {paragraph}
            </p>
          ))}
        </section>

        <section className="mb-8">
          <SectionTitle>{locale.section2Title}</SectionTitle>
          <ul className="list-disc space-y-3 pl-5 text-[14.5px]">
            {locale.mainRisks.map((risk) => (
              <li key={risk.title}>
                <em>{risk.title}</em> {risk.text}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-8">
          <SectionTitle>{locale.section3Title}</SectionTitle>
          <ul className="list-disc space-y-2 pl-5 text-[14.5px]">
            {locale.warnings.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="mb-6">
          <p className="mb-3 text-[14.5px]">{locale.confirmationIntro}</p>
          <ul className="mb-6 list-disc space-y-2 pl-5 text-[14.5px]">
            {locale.confirmationPoints.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <div className="rounded-xl border border-[var(--border)] bg-[#f7f9f9] px-5 py-6 sm:px-8">
            <div className="mx-auto w-full max-w-[50%]">
              <ConfirmationForm
                form={form}
                fieldErrors={fieldErrors}
                fields={locale.fields}
                onChange={onChange}
                disabled={isLoading}
              />
            </div>

            <div className="mx-auto mt-6 flex w-full max-w-[50%] flex-col items-stretch gap-2">
              {isError && errorMessage ? (
                <p className="text-center text-[13px] text-[#b42318]">
                  {errorMessage}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={isSubmitDisabled}
                className="inline-flex w-full items-center justify-center rounded-lg bg-[var(--accent)] px-5 py-2.5 text-[14px] font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isLoading ? <Spinner /> : locale.submit}
              </button>
              <p className="text-center text-[12px] leading-snug text-[var(--muted)]">
                {locale.submitHint}
              </p>
            </div>
          </div>
        </section>
      </form>

      {isSuccess ? (
        <SuccessModal
          title={locale.successTitle}
          closeLabel={locale.closeLabel}
          onClose={onCloseSuccess}
        />
      ) : null}
    </div>
  );
}
